import { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useDetectOutsideClick } from '../../../../../generic/hooks/useDetectOutsideClick'
import FilterIcon from '../../../../../generic/icons/IconLib/FilterIcon'
import { DateRangePicker } from '../../../../../generic/ui/DatePicker'
import ProductForSortingSelector from '../../../../../generic/ui/Selectors/ProductForSortingSelector'
import GrowerSelector from '../../../../../generic/ui/Selectors/GrowerSelector'
import { FormShell, FormGrid, FormField, FormFooter } from '../../../../../generic/ui/FormLayout'
import { AccentButton } from '../../../../../generic/ui/Button/AccentButton'
import { IGrowerItem } from '../../../../../modules/API/Defaults/models/growers'
import { IProductListItemNode } from '../../../../../modules/API/Defaults/models/productListNode'

type Props = {
  products: IProductListItemNode[]
  growers: IGrowerItem[]
  grower: number
  setGrower: (id: number) => void
  isGrower: boolean
  onSearch: (productID: number, dates: Date[], growerID: number) => void
}

const noop = () => {}

const KnisaLinesFilterButton = ({
  products,
  growers,
  grower,
  setGrower,
  isGrower,
  onSearch,
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)
  const [range, setRange] = useState<[Date | null, Date | null]>([new Date(), new Date()])
  const [product, setProduct] = useState<IProductListItemNode | null>(null)

  useDetectOutsideClick(wrapperRef, () => setOpen(false))

  const growerObj = useMemo(
    () => growers.find((g) => g.GrowerID === grower) ?? null,
    [growers, grower],
  )

  const activeCount = (product ? 1 : 0) + (grower ? 1 : 0)

  const handleSearch = () => {
    const [from, to] = range
    onSearch(product?.ProductID ?? 0, [from ?? new Date(), to ?? from ?? new Date()], grower)
    setOpen(false)
  }

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        type='button'
        aria-haspopup='dialog'
        aria-expanded={open}
        aria-label='סינון'
        $active={activeCount > 0}
        $open={open}
        onClick={() => setOpen((v) => !v)}
      >
        <FilterIcon />
        {activeCount > 0 && <Count>{activeCount}</Count>}
      </Trigger>

      {open && (
        <Panel role='dialog'>
          <FormShell>
            <FormGrid>
              <FormField label='טווח תאריכים' fullWidth>
                <DateRangePicker
                  selected={[range[0] ?? new Date(), range[1] ?? new Date()]}
                  setDate={noop}
                  setDateEnd={noop}
                  onChange={setRange}
                />
              </FormField>
              <FormField label='מוצר' fullWidth>
                <ProductForSortingSelector
                  items={products}
                  value={product}
                  onSelect={setProduct}
                />
              </FormField>
              <FormField label='מגדל' fullWidth>
                <GrowerSelector
                  growers={growers}
                  value={growerObj}
                  onSelect={(g) => setGrower(g?.GrowerID ?? 0)}
                  disabled={isGrower}
                />
              </FormField>
            </FormGrid>
            <FormFooter>
              <AccentButton type='button' onClick={handleSearch} fullWidth={false}>
                חפש
              </AccentButton>
            </FormFooter>
          </FormShell>
        </Panel>
      )}
    </Wrapper>
  )
}

export default KnisaLinesFilterButton

const Wrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`

const Trigger = styled.button<{ $active: boolean; $open: boolean }>`
  position: relative;
  box-sizing: border-box;
  width: 2.875rem;
  height: 2.875rem;
  background: ${({ theme, $active }) => ($active ? theme.colors.accentSoft : theme.colors.surface)};
  border: 1px solid
    ${({ theme, $active, $open }) => ($open || $active ? theme.colors.ink : theme.colors.inputBorder)};
  border-radius: 0.875rem;
  color: ${({ theme, $active }) => ($active ? theme.colors.ink : theme.colors.icon)};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const Count = styled.span`
  position: absolute;
  top: -0.375rem;
  inset-inline-start: -0.375rem;
  min-width: 1.125rem;
  height: 1.125rem;
  padding: 0 0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.ink};
  border-radius: 999px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  font-weight: 600;
`

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  inset-inline-end: 0;
  z-index: 30;
  width: 22rem;
  max-width: calc(100vw - 1.5rem);
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: 1rem;
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 0.875rem;
  direction: rtl;

  /* Calendar popper must sit above the sticky FormFooter (z-index: 2),
     otherwise the footer's opaque background clips the date picker. */
  & .react-datepicker-popper {
    z-index: 20;
  }
`
